import type { Device, OverviewData, Role } from '@/types';
import type { MonitorIncident, MonitorStatus } from '@/types/assistant';
import { timeAgo } from '@/lib/assistant';

export type CompanionExpression = 'idle' | 'blink' | 'left' | 'right' | 'wave' | 'notice';

export interface CompanionItem {
  id: string;
  title: string;
  detail: string;
  href: string;
  action: string;
  tone: 'warn' | 'info';
}

export function buildCompanionBrief(data: OverviewData, role: Role) {
  const items: CompanionItem[] = [];
  const offline = Math.max(0, data.devices.total - data.devices.online);
  const number = (n: number) => n.toLocaleString('zh-TW');
  if (data.media.failed > 0) items.push({
    id: 'media', title: `${number(data.media.failed)} 個檔案傳輸失敗`,
    detail: '先看失敗原因，確認檔案是否已保存。',
    href: '/media?status=failed', action: '查看失敗檔案', tone: 'warn',
  });
  if (offline > 0) items.push({
    id: 'devices', title: `${number(offline)} 台裝置離線`,
    detail: '確認裝置電源與網路，查看最後回報時間。',
    href: '/devices?status=offline', action: '檢查離線裝置', tone: 'warn',
  });
  if (data.jobs.failed > 0) items.push({
    id: 'jobs', title: `${number(data.jobs.failed)} 個背景工作失敗`,
    detail: '部分驗證或歸檔工作未完成，請查看總覽。',
    href: '/overview', action: '查看工作狀態', tone: 'warn',
  });
  if (data.events.unreviewed > 0) items.push({
    id: 'events', title: `${number(data.events.unreviewed)} 筆事件待確認`,
    detail: role === 'viewer' ? '可先查看內容，再交由有權限的同事判讀。' : '可以從尚未判讀的事件開始。',
    href: '/events?state=unreviewed', action: role === 'viewer' ? '查看待確認事件' : '前往判讀事件', tone: 'info',
  });
  if (data.events.site_pending > 0) items.push({
    id: 'sites', title: `${number(data.events.site_pending)} 筆事件待確認場域`,
    detail: '檢查事件來源，確認所屬場域。',
    href: '/events?site_resolution=pending', action: '查看待確認場域', tone: 'info',
  });
  return {
    items,
    title: items.length ? `有 ${items.length} 項狀態值得留意` : data.devices.total === 0
      ? '還沒有已認領的裝置' : '裝置與傳輸目前沒有異常',
    detail: items[0]?.title ?? (data.devices.total === 0
      ? '可以先到裝置頁查看待認領清單。'
      : `${number(data.devices.online)} 台裝置在線上，${number(data.media.in_flight)} 個檔案傳輸中。`),
    href: items[0]?.href ?? '/devices',
    action: items[0]?.action ?? '查看裝置',
    needsAttention: items.some((item) => item.tone === 'warn'),
  };
}

export function companionDataIsCurrent(updatedAt: number, now: number, online: boolean, hasError: boolean) {
  return online && !hasError && updatedAt > 0 && now - updatedAt < 60_000;
}

export interface CompanionPreferences { motion: boolean; loginBrief: boolean }
export const DEFAULT_COMPANION_PREFERENCES: CompanionPreferences = { motion: true, loginBrief: true };
/* /auth/me 與登入回應只有帳號名稱，沒有 id，所以用帳號名稱區分。 */
export const companionBriefKey = (account: string) => `edgeai:companion:brief:v1:${account}`;

export function parseCompanionPreferences(raw: string | null): CompanionPreferences {
  try {
    const value: unknown = JSON.parse(raw ?? 'null');
    if (!value || typeof value !== 'object') return DEFAULT_COMPANION_PREFERENCES;
    const prefs = value as Partial<CompanionPreferences>;
    return {
      motion: typeof prefs.motion === 'boolean' ? prefs.motion : true,
      loginBrief: typeof prefs.loginBrief === 'boolean' ? prefs.loginBrief : true,
    };
  } catch {
    return DEFAULT_COMPANION_PREFERENCES;
  }
}

/* ── 登入報告：整體狀況與各裝置 ──
   全部由規則組出來，不呼叫模型；只寫已查到的事，查不到的就不下結論。 */

export type ReportTone = 'ok' | 'warn' | 'info' | 'muted';

export interface ReportFact {
  id: 'devices' | 'events' | 'media' | 'jobs' | 'monitor';
  label: string;
  value: string;
  tone: ReportTone;
  href?: string;
}

export interface ReportDevice {
  id: string;
  name: string;
  online: boolean;
  status: string;
  meta: string[];
  issues: string[];
  tone: ReportTone;
  href: string;
}

export interface CompanionReport {
  headline: string;
  tone: ReportTone;
  facts: ReportFact[];
  devices: ReportDevice[];
  /** 沒列出來的裝置數。 */
  moreDevices: number;
}

/** 後端監控 storage_low 的預設門檻。監控還沒判斷或沒在跑時，用它補上提醒。 */
export const STORAGE_LOW_PCT = 10;
const ISSUE_LABEL: Record<string, string> = { storage_low: 'SD 卡快滿了', upload_failures: '上傳一直失敗' };

/** SD 剩餘百分比；缺總容量就回 null，不猜（和後端 assistant_rules.storage_pct 相同）。 */
export function storagePct(telemetry: Record<string, unknown> | null | undefined): number | null {
  const total = telemetry?.sd_total_mb;
  const free = telemetry?.sd_free_mb;
  if (typeof total !== 'number' || typeof free !== 'number' || !(total > 0)) return null;
  return Math.max(0, Math.min(100, (free * 100) / total));
}

export function buildCompanionReport({ overview, devices, incidents, monitor, now = Date.now(), maxDevices = 5 }: {
  overview: OverviewData; devices?: Device[] | null; incidents?: MonitorIncident[] | null;
  monitor?: MonitorStatus | null; now?: number; maxDevices?: number;
}): CompanionReport {
  const number = (n: number) => n.toLocaleString('zh-TW');
  const { events, media, jobs } = overview;

  // 離線已經寫在裝置狀態上，異常清單不重複列
  const issuesByDevice = new Map<string, Set<string>>();
  for (const incident of incidents ?? []) {
    if (incident.status !== 'open' || incident.rule === 'device_offline') continue;
    const issues = issuesByDevice.get(incident.device_key) ?? new Set<string>();
    issues.add(ISSUE_LABEL[incident.rule] ?? incident.title);
    issuesByDevice.set(incident.device_key, issues);
  }

  // 待認領的裝置還不屬於平台，和裝置頁一樣只算已認領（沒有 claim_state 的舊資料視為已認領）
  const rows = (devices ?? []).filter((d) => (d.claim_state ?? 'claimed') === 'claimed').map((d) => {
    const pct = storagePct(d.telemetry);
    const issues = new Set(issuesByDevice.get(d.device_id));
    if (pct !== null && pct <= STORAGE_LOW_PCT) issues.add(ISSUE_LABEL.storage_low);
    const meta: string[] = [];
    if (!d.online) meta.push(d.last_seen_at ? `最後回報 ${timeAgo(d.last_seen_at, now)}` : '還沒有回報紀錄');
    if (pct !== null) meta.push(pct > 0 && pct < 1 ? 'SD 剩不到 1%' : `SD 剩 ${Math.round(pct)}%`);
    if (d.firmware_version) meta.push(`韌體 ${d.firmware_version}`);
    const rank = !d.online ? 0 : issues.size ? 1 : 2;
    const row: ReportDevice = {
      id: d.device_id, name: d.label || d.device_id, online: d.online, status: d.online ? '在線' : '離線',
      meta, issues: [...issues], tone: rank < 2 ? 'warn' : 'ok', href: d.online ? '/devices' : '/devices?status=offline',
    };
    return { row, rank };
  }).sort((a, b) => a.rank - b.rank || a.row.name.localeCompare(b.row.name, 'zh-TW'));

  // 清單載入後台數以清單為準，報告裡的離線數和下面列出的裝置才對得起來
  const total = devices ? rows.length : overview.devices.total;
  const online = devices ? rows.filter(({ row }) => row.online).length : Math.min(overview.devices.online, total);
  const offline = total - online;
  const storageLow = rows.filter(({ row }) => row.issues.includes(ISSUE_LABEL.storage_low)).length;
  const uploadStuck = rows.filter(({ row }) => row.issues.includes(ISSUE_LABEL.upload_failures)).length;
  const problems = [
    offline > 0 ? `${number(offline)} 台離線` : '',
    storageLow > 0 ? `${number(storageLow)} 台 SD 卡快滿了` : '',
    uploadStuck > 0 ? `${number(uploadStuck)} 台上傳一直失敗` : '',
    media.failed > 0 ? `${number(media.failed)} 個檔案傳輸失敗` : '',
    jobs.failed > 0 ? `${number(jobs.failed)} 個背景工作失敗` : '',
  ].filter(Boolean);

  let headline: string;
  let tone: ReportTone;
  if (problems.length) {
    const extra = problems.length > 3 ? `，還有 ${problems.length - 3} 項` : '';
    const deviceProblem = rows.some(({ rank }) => rank < 2);
    const healthy = deviceProblem ? rows.filter(({ rank }) => rank === 2).length : 0;
    headline = `${problems.slice(0, 3).join('、')}${extra}${healthy ? `，其他 ${number(healthy)} 台運作正常` : ''}。`;
    tone = 'warn';
  } else if (total === 0) {
    headline = '還沒有已認領的裝置，可以先到裝置頁查看待認領清單。';
    tone = 'info';
  } else {
    const fleet = total === 1 ? '1 台裝置在線上' : `${number(total)} 台裝置都在線上`;
    headline = `${fleet}，傳輸沒有異常${events.unreviewed ? `；有 ${number(events.unreviewed)} 筆事件待確認` : ''}。`;
    tone = events.unreviewed ? 'info' : 'ok';
  }

  const eventParts = [`24 小時內 ${number(events.last24h)} 筆`];
  if (events.unreviewed) eventParts.push(`待確認 ${number(events.unreviewed)} 筆`);
  if (events.site_pending) eventParts.push(`待確認場域 ${number(events.site_pending)} 筆`);
  const facts: ReportFact[] = [
    {
      id: 'devices', label: '裝置',
      value: total === 0 ? '還沒有已認領的裝置' : offline ? `${number(online)}/${number(total)} 台在線，${number(offline)} 台離線`
        : total === 1 ? '1 台在線上' : `${number(total)} 台都在線上`,
      tone: offline ? 'warn' : total === 0 ? 'muted' : 'ok',
      href: offline ? '/devices?status=offline' : '/devices',
    },
    {
      id: 'events', label: '事件', value: eventParts.join('，'),
      tone: events.unreviewed || events.site_pending ? 'info' : 'ok',
      href: events.unreviewed ? '/events?state=unreviewed' : events.site_pending ? '/events?site_resolution=pending' : '/events',
    },
    {
      id: 'media', label: '檔案',
      value: media.failed ? `${number(media.failed)} 個傳輸失敗${media.in_flight ? `，${number(media.in_flight)} 個傳輸中` : ''}`
        : media.in_flight ? `${number(media.in_flight)} 個傳輸中，沒有失敗` : '沒有進行中的傳輸',
      tone: media.failed ? 'warn' : 'ok',
      href: media.failed ? '/media?status=failed' : '/media',
    },
    {
      id: 'jobs', label: '背景工作',
      value: jobs.failed ? `${number(jobs.failed)} 個失敗` : jobs.queued ? `${number(jobs.queued)} 個排隊中，沒有失敗` : '沒有失敗',
      tone: jobs.failed ? 'warn' : 'ok',
      href: jobs.failed ? '/overview' : undefined,
    },
  ];
  if (monitor) facts.push({
    id: 'monitor', label: '異常監控',
    value: monitor.alive ? `運作中，${timeAgo(monitor.last_run_at, now)}檢查過`
      : `${monitor.last_run_at ? `最後檢查是 ${timeAgo(monitor.last_run_at, now)}` : '還沒有執行紀錄'}，異常通知可能延遲`,
    tone: monitor.alive ? 'ok' : 'warn',
  });

  return {
    headline, tone, facts,
    devices: rows.slice(0, maxDevices).map(({ row }) => row),
    moreDevices: Math.max(0, rows.length - maxDevices),
  };
}

/* ── 電腦版浮動小哨的位置 ──
   只記左右哪一側和離底部多遠；視窗縮放時再夾回可見範圍，不改存好的值。 */

export type DockSide = 'left' | 'right';
export interface DockPosition { side: DockSide; bottom: number }
export const DEFAULT_DOCK_POSITION: DockPosition = { side: 'right', bottom: 24 };
export const DOCK_MARGIN = 16;
export const companionDockKey = (account: string) => `edgeai:companion:dock:v1:${account}`;

export function parseDockPosition(raw: string | null): DockPosition {
  try {
    const value: unknown = JSON.parse(raw ?? 'null');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return DEFAULT_DOCK_POSITION;
    const position = value as Partial<DockPosition>;
    return {
      side: position.side === 'left' ? 'left' : 'right',
      bottom: typeof position.bottom === 'number' && Number.isFinite(position.bottom)
        ? Math.max(DOCK_MARGIN, Math.round(position.bottom)) : DEFAULT_DOCK_POSITION.bottom,
    };
  } catch {
    return DEFAULT_DOCK_POSITION;
  }
}

/** 底部至少留 16px，頂端要放得下整張卡片。 */
export function clampDockBottom(bottom: number, viewportHeight: number, dockHeight: number) {
  const highest = Math.max(DOCK_MARGIN, viewportHeight - dockHeight - DOCK_MARGIN);
  return Math.round(Math.min(Math.max(bottom, DOCK_MARGIN), highest));
}

/** 放開時吸附到較近的一側；可用區域從側欄右緣算起。 */
export function dockSideFor(centerX: number, leftEdge: number, viewportWidth: number): DockSide {
  return centerX < (leftEdge + viewportWidth) / 2 ? 'left' : 'right';
}

/** 報告泡泡放在小哨上方；上方放不下而下方空間較大時才改放下方。gap 要和 companion.css 的泡泡間距一致。 */
export function bubblePlacement(dockBottom: number, dockHeight: number, viewportHeight: number, gap = 10) {
  const above = viewportHeight - dockBottom - dockHeight - gap - DOCK_MARGIN;
  const below = dockBottom - gap - DOCK_MARGIN;
  return above >= Math.min(below, 420)
    ? { place: 'above' as const, maxHeight: above }
    : { place: 'below' as const, maxHeight: below };
}
